import { createContext, type ReactNode, useContext, useMemo } from 'react';
import type { PolicyActionEnum } from '../actions.js';
import { createAllowedActionsEvaluator, DENY_ALL_EVALUATOR, type PolicyEvaluator } from '../evaluator.js';

/** Defaults to deny-all, so a component rendered outside a provider shows nothing gated. */
const PolicyEvaluatorContext = createContext<PolicyEvaluator>(DENY_ALL_EVALUATOR);
PolicyEvaluatorContext.displayName = 'PolicyEvaluatorContext';

export type PolicyProviderProps = {
  /** The actions the server resolved as allowed for the user. Keep the array reference stable. */
  allowedActions: readonly string[];
  children: ReactNode;
};

export function PolicyProvider({ allowedActions, children }: PolicyProviderProps): ReactNode {
  const evaluator = useMemo(() => createAllowedActionsEvaluator(allowedActions), [allowedActions]);
  return <PolicyEvaluatorContext.Provider value={evaluator}>{children}</PolicyEvaluatorContext.Provider>;
}

export function usePolicyEvaluator(): PolicyEvaluator {
  return useContext(PolicyEvaluatorContext);
}

/** True when every action is allowed. UI gating only; the API enforces access. */
export function useCan(...actions: PolicyActionEnum[]): boolean {
  return usePolicyEvaluator().isAllowedAll(actions);
}

export type CanProps = {
  actions: PolicyActionEnum[];
  children: ReactNode;
  fallback?: ReactNode;
};

export function Can({ actions, children, fallback = null }: CanProps): ReactNode {
  return useCan(...actions) ? children : fallback;
}
